<template>
  <v-dialog
    v-model="internalValue"
    max-width="600"
    persistent
  >
    <v-card>
      <v-card-title class="text-h6">
        Assign Clients to Yourself
      </v-card-title>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          dismissible
          class="mb-4"
          @input="error = null"
        >
          {{ error }}
        </v-alert>

        <v-alert
          v-if="success"
          type="success"
          dismissible
          class="mb-4"
          @input="success = null"
        >
          {{ success }}
        </v-alert>

        <div class="text-body-2 mb-4">
          Select clients from the available networks to assign to yourself.
          This will allow you to view their pacing data on the dashboard.
        </div>

        <!-- Loading state -->
        <div
          v-if="loadingClients"
          class="text-center py-8"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
          <div class="mt-2 text-body-2 grey--text">
            Loading available clients...
          </div>
        </div>

        <!-- Client selection -->
        <div v-else-if="availableClients.length > 0">
          <v-text-field
            v-model="searchText"
            label="Search clients"
            outlined
            dense
            clearable
            hide-details
            class="mb-4"
          >
            <template v-slot:prepend-inner>
              <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
            </template>
          </v-text-field>

          <div class="clients-list">
            <v-list dense>
              <v-list-item
                v-for="client in filteredClients"
                :key="client.clientId"
                @click="toggleClient(client)"
              >
                <v-list-item-action>
                  <Checkbox
                    :input-value="isClientSelected(client)"
                    @change="toggleClient(client)"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ client.clientName }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ client.billingCode }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <div class="mt-4 text-body-2 grey--text">
            {{ selectedClients.length }} client(s) selected
          </div>
        </div>

        <!-- No clients available -->
        <div
          v-else
          class="text-center py-8"
        >
          <v-icon
            size="48"
            color="grey lighten-1"
          >
            mdi-information-outline
          </v-icon>
          <div class="mt-2 text-body-2 grey--text">
            No clients available to assign
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :disabled="selectedClients.length === 0 || saving"
          :loading="saving"
          @click="saveAssignments"
        >
          Assign {{ selectedClients.length > 0 ? `(${selectedClients.length})` : '' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import api from '@/store/api'
import Checkbox from '@/tools/components/Checkbox'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

export default {
  name: 'AssignClientsDialog',
  components: {
    Checkbox,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loadingClients: false,
      saving: false,
      error: null,
      success: null,
      searchText: '',
      availableClients: [],
      selectedClients: [],
    }
  },
  computed: {
    ...mapAuthGetters([
      'currentUser',
      'self',
    ]),
    internalValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    filteredClients() {
      if (!this.searchText || this.searchText.trim() === '') {
        return this.availableClients
      }

      const search = this.searchText.toLowerCase().trim()
      return this.availableClients.filter((client) => client.clientName.toLowerCase().includes(search)
          || client.billingCode.toLowerCase().includes(search))
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.loadClients()
        } else {
          this.resetDialog()
        }
      },
    },
  },
  methods: {
    async loadClients() {
      this.loadingClients = true
      this.error = null

      try {
        // Get all clients and current assignments in parallel
        const personId = this.self?.personId
        const [clientsResponse, assignmentsResponse] = await Promise.all([
          api.getClients(),
          personId ? api.getClientAssignments(personId) : Promise.resolve(null),
        ])

        const { content, error } = clientsResponse || {}

        if (error) {
          this.error = 'Failed to load clients'
          this.availableClients = []
          return
        }

        // Filter out administrative clients
        this.availableClients = (content || []).filter((client) => !client.isAdministrative)

        // Pre-select currently assigned clients
        const assignments = assignmentsResponse?.content || []
        if (assignments.length > 0) {
          const assignedClientIds = new Set(assignments.map((a) => a.clientId))
          this.selectedClients = this.availableClients.filter((c) => assignedClientIds.has(c.clientId))
        }
      } catch (err) {
        this.$log.error('Error loading clients:', err)
        this.error = 'An error occurred while loading clients'
        this.availableClients = []
      } finally {
        this.loadingClients = false
      }
    },

    isClientSelected(client) {
      return this.selectedClients.some((c) => c.clientId === client.clientId)
    },

    toggleClient(client) {
      const index = this.selectedClients.findIndex((c) => c.clientId === client.clientId)

      if (index > -1) {
        this.selectedClients.splice(index, 1)
      } else {
        this.selectedClients.push(client)
      }
    },

    async saveAssignments() {
      if (this.selectedClients.length === 0) return

      this.saving = true
      this.error = null
      this.success = null

      try {
        const { email: personEmail } = this.currentUser
        const { personId } = this.self

        if (!personId) {
          this.error = 'Person ID not found. Please refresh and try again.'
          this.saving = false
          return
        }

        this.$log.info('Assigning clients:', {
          personEmail,
          personId,
          clientCount: this.selectedClients.length,
          clients: this.selectedClients.map((c) => ({ id: c.clientId, name: c.clientName })),
        })

        // Assign each selected client
        const promises = this.selectedClients.map(async (client) => {
          const payload = {
            action_type: 'add',
            person_email: personEmail,
            person_id: personId,
            client_id: client.clientId,
            role_id: 1, // Account Manager role
            active_flag: true,
          }

          this.$log.info('Posting client assignment:', payload)

          const response = await api.post('/timesheet/rpc/modify_client_person', payload)

          this.$log.info('Assignment response:', response)

          return response
        })

        const results = await Promise.all(promises)

        // Check for any errors in responses
        const errors = results.filter((r) => r && r.error)
        if (errors.length > 0) {
          this.$log.error('Some assignments failed:', errors)
          this.error = `Failed to assign ${errors.length} of ${this.selectedClients.length} client(s)`
          return
        }

        this.success = `Successfully assigned ${this.selectedClients.length} client(s)`

        // Wait a moment to show success message
        setTimeout(() => {
          this.$emit('assigned')
          this.close()
        }, 1500)
      } catch (err) {
        this.$log.error('Error assigning clients:', err)
        this.error = `Failed to assign clients: ${err.message || 'Unknown error'}`
      } finally {
        this.saving = false
      }
    },

    resetDialog() {
      this.searchText = ''
      this.selectedClients = []
      this.error = null
      this.success = null
    },

    close() {
      this.internalValue = false
    },
  },
}
</script>

<style scoped lang="scss">
.clients-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
